import { CalendarIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
    ClientId,
    CommunityId,
    formatTourDate,
    formatTourTime,
    splitTours,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import React, { useCallback, useMemo, useState } from "react";
import { breakNewlines } from "util/text";
import { DeleteButton, EditButton } from "../ui/action-button";
import { ClampedText } from "../ui/clamped-text";
import { TourEditor } from "./tour-editor";
import { TourDto } from "./types";

interface ToursSectionProps {
    tours: TourDto[];
    clientId: ClientId;
    clientName?: string;
    communityId: CommunityId;
    communityName?: string;
    onToursChanged: () => void;
}

const TourItem: React.FC<{
    tour: TourDto;
    onEdit: (tour: TourDto) => void;
    onDelete: (tour: TourDto) => void;
    onToggleVisibility: (tour: TourDto) => void;
}> = ({ tour, onEdit, onDelete, onToggleVisibility }) => {
    const handleEdit = useCallback(() => {
        onEdit(tour);
    }, [onEdit, tour]);

    const handleDelete = useCallback(() => {
        onDelete(tour);
    }, [onDelete, tour]);

    return (
        <li className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-start">
                <div className="flex-1">
                    <span className="text-base">
                        {formatTourDate(tour.tourDate)}
                        {formatTourTime(tour.tourTime)}
                    </span>
                    {tour.familyNotes ? (
                        <div className="mt-2 space-y-0.5">
                            <div className="text-sm font-medium text-gray-500">
                                Tour Notes
                            </div>
                            <div className="text-sm text-gray-900">
                                <ClampedText lines={3}>
                                    {breakNewlines(tour.familyNotes)}
                                </ClampedText>
                            </div>
                        </div>
                    ) : null}
                    {tour.privateNotes ? (
                        <div className="mt-2 space-y-0.5">
                            <div className="text-sm font-medium text-gray-500">
                                Internal Notes
                            </div>
                            <div className="text-sm text-gray-900">
                                <ClampedText lines={3}>
                                    {breakNewlines(tour.privateNotes)}
                                </ClampedText>
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className="flex items-center">
                    {!(tour.showOnFamilyView ?? true) ? (
                        <span className="text-xs text-gray-400 mr-2">
                            Hidden from family
                        </span>
                    ) : null}
                    <EditButton backgroundColor="bg-white" onClick={handleEdit} />
                    <DeleteButton
                        backgroundColor="bg-white"
                        onClick={handleDelete}
                        confirm={{
                            title: "Delete Tour",
                            message: "Are you sure you want to delete this tour?",
                        }}
                    />
                </div>
            </div>
        </li>
    );
};

export const ToursSection: React.FC<ToursSectionProps> = props => {
    const { tours, clientId, clientName, communityId, communityName, onToursChanged } =
        props;
    const [editingTour, setEditingTour] = useState<TourDto | null | "new">(null);

    const handleAddTour = useCallback(() => {
        setEditingTour("new");
    }, []);

    const handleEditTour = useCallback((tour: TourDto) => {
        setEditingTour(tour);
    }, []);

    const handleClose = useCallback(() => {
        setEditingTour(null);
    }, []);

    const handleSaved = useCallback(() => {
        onToursChanged();
    }, [onToursChanged]);

    const trpcClient = trpc.useTRPC();
    const deleteMutation = useMutation({
        ...trpcClient.tours.delete.mutationOptions(),
        onSuccess: () => {
            onToursChanged();
        },
    });

    const updateMutation = useMutation({
        ...trpcClient.tours.update.mutationOptions(),
        onSuccess: () => {
            onToursChanged();
        },
    });

    const handleDeleteTour = useCallback(
        (tour: TourDto) => {
            deleteMutation.mutate({ id: tour.id });
        },
        [deleteMutation],
    );

    const handleToggleVisibility = useCallback(
        (tour: TourDto) => {
            updateMutation.mutate({
                id: tour.id,
                showOnFamilyView: !(tour.showOnFamilyView ?? true),
            });
        },
        [updateMutation],
    );

    const { upcoming, past } = useMemo(() => splitTours(tours), [tours]);

    const hasBothSections = upcoming.length > 0 && past.length > 0;

    return (
        <div className="max-w-2xl">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                    <CalendarIcon className="h-4 w-4" />
                    <span>Tours</span>
                </h4>
                <button
                    onClick={handleAddTour}
                    className="flex items-center space-x-1 text-sm text-brand-600 hover:text-brand-800">
                    <PlusIcon className="h-4 w-4" />
                    <span>Add Tour</span>
                </button>
            </div>

            {tours.length > 0 ? (
                <div>
                    {upcoming.length > 0 ? (
                        <>
                            {hasBothSections ? (
                                <div className="pb-1 text-xs font-medium text-gray-500">
                                    Upcoming
                                </div>
                            ) : null}
                            <ul className="space-y-2">
                                {upcoming.map(tour => (
                                    <TourItem
                                        key={tour.id}
                                        tour={tour}
                                        onEdit={handleEditTour}
                                        onDelete={handleDeleteTour}
                                        onToggleVisibility={handleToggleVisibility}
                                    />
                                ))}
                            </ul>
                        </>
                    ) : null}
                    {past.length > 0 ? (
                        <>
                            {hasBothSections ? (
                                <div className="pt-2 pb-1 text-xs font-medium text-gray-500">
                                    Past
                                </div>
                            ) : null}
                            <ul className="space-y-2">
                                {past.map(tour => (
                                    <TourItem
                                        key={tour.id}
                                        tour={tour}
                                        onEdit={handleEditTour}
                                        onDelete={handleDeleteTour}
                                        onToggleVisibility={handleToggleVisibility}
                                    />
                                ))}
                            </ul>
                        </>
                    ) : null}
                </div>
            ) : (
                <p className="text-sm text-gray-500">No tours scheduled</p>
            )}

            {editingTour !== null ? (
                <TourEditor
                    tour={editingTour === "new" ? null : editingTour}
                    clientId={clientId}
                    clientName={clientName}
                    communityId={communityId}
                    communityName={communityName}
                    onSaved={handleSaved}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
};
